'use client'

import type {
  AnonymousUser,
  AuthenticatedUser,
  User
} from '@algoacademy/backend/modules/auth/integrations/better-auth/get-user'
import type { Permission } from '@algoacademy/backend/permissions'
import { createContext, type ReactNode, useContext } from 'react'
import { authClient } from '@/app/_auth/auth-client'

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

type AuthContextValue = {
  user: User
  status: AuthStatus
  signIn: typeof authClient.signIn
  signOut: typeof authClient.signOut
}

export type ServerAuthData =
  | Omit<AnonymousUser, 'hasPermissions'>
  | Omit<AuthenticatedUser, 'hasPermissions'>

const AuthContext = createContext<AuthContextValue | null>(null)

const ANONYMOUS_USER: AnonymousUser = {
  isLoggedIn: false,
  id: null,
  name: null,
  email: null,
  imageUrl: null,
  role: null,
  permissions: [],
  hasPermissions: () => false
} as const

function toAuthenticatedUser(
  data: Extract<ServerAuthData, { isLoggedIn: true }>
): AuthenticatedUser {
  const { permissions } = data
  return {
    isLoggedIn: true,
    id: data.id,
    name: data.name,
    email: data.email,
    imageUrl: data.imageUrl,
    role: data.role,
    permissions,
    hasPermissions: (required: Permission[]) =>
      required.every(p => permissions.some(cached => cached.dbName === p.dbName))
  }
}

function resolveAuth(
  serverAuth: ServerAuthData,
  clientSession: unknown,
  isPending: boolean
): AuthContextValue {
  const base = { signIn: authClient.signIn, signOut: authClient.signOut }

  if (isPending) {
    return serverAuth.isLoggedIn
      ? { ...base, user: toAuthenticatedUser(serverAuth), status: 'authenticated' }
      : { ...base, user: ANONYMOUS_USER, status: 'unauthenticated' }
  }

  if (!clientSession || !serverAuth.isLoggedIn) {
    return { ...base, user: ANONYMOUS_USER, status: 'unauthenticated' }
  }

  return { ...base, user: toAuthenticatedUser(serverAuth), status: 'authenticated' }
}

export function AuthProvider({
  children,
  serverAuth
}: {
  children: ReactNode
  serverAuth: ServerAuthData
}) {
  const { data: clientSession, isPending } = authClient.useSession()
  const value = resolveAuth(serverAuth, clientSession?.user, isPending)

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
